import { authLogger } from "../../utils/logger";import { cognitoRegistrationService } from './cognitoRegistrationService';
import { authService } from './authService';
import { emailService } from './emailService';

import type { 
  SignupResult, 
  EmailVerificationResult, 
} from '../../types';

export interface CompleteRegistrationData {
  email: string;
  password: string;
}

export interface SignupUserData {
  family_name: string;
  email: string;
  given_name: string;
  'custom:tenantId': string;
  'custom:subscriptionTier'?: string;
  password: string;
}

export interface TenantSetupData {
  tenantId: string;
  email: string;
}

export interface RegistrationResult {
  success: boolean;
  message: string;
  tenantId: string;
  userId?: string;
}

// Dynamic mock mode checker
const getMockMode = (): boolean => {
  if (typeof window !== 'undefined') {
    if (typeof window.__TEST_MOCK_MODE__ !== 'undefined') {
      return window.__TEST_MOCK_MODE__;
    }
    
    try {
      const testMode = localStorage.getItem('TEST_MOCK_MODE');
      if (testMode !== null) {
        return testMode === 'true';
      }
    } catch (e) {
      // localStorage might not be available
    }
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const mockParam = urlParams.get('mock');
      if (mockParam !== null) {
        return mockParam === 'true';
      }
    } catch (e) {
      // URL parsing might fail
    }
  }
  
  return import.meta.env.VITE_MOCK_MODE === 'true';
};

export const registrationService = {
  /**
   * Phase 1: Create user account and trigger email verification
   */
  async signupUser(data: SignupUserData): Promise<SignupResult> {
    if (getMockMode()) {
      authLogger.info('🔍 RegistrationService: Using mock mode for signup');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In mock mode, simulate existing user scenario for demo email
      if (data.email === 'demo@fabrik28.ai') {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }
      
      // For other emails, simulate checking if user might already exist
      // This helps test the existing user flow in mock mode
      const existingEmails = ['test@example.com', 'user@test.com', 'existing@fabrik28.ai'];
      if (existingEmails.includes(data.email)) {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }
      
      return {
        success: true,
        message: 'Account created successfully (mock mode)',
        tenantId: data['custom:tenantId'],
      };
    }

    try {
      authLogger.info('🔍 RegistrationService: Starting user signup for:', data.email);
      
      // Create Cognito user with email verification
      const cognitoResult = await cognitoRegistrationService.registerUser({
        contactPerson: `${data.given_name} ${data.family_name}`,
        email: data.email,
        password: data.password
      });
      
      if (!cognitoResult.success) {
        throw new Error(cognitoResult.message || 'Failed to create user account');
      }
      
      authLogger.info('🔍 RegistrationService: User signup completed:', cognitoResult.userId);
      
      return {
        success: true,
        message: 'Account created successfully. Please verify your email.',
        tenantId: cognitoResult.tenantId,
        userId: cognitoResult.userId
      };
      
    } catch (error) {
      authLogger.error('❌ RegistrationService: Signup failed:', error);
      
      // Provide user-friendly error messages
      if (error instanceof Error) {
        if (error.message.includes('UsernameExistsException') || error.message.includes('already exists')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        }
        if (error.message.includes('InvalidPasswordException')) {
          throw new Error('Password does not meet requirements. Please use at least 8 characters with uppercase, lowercase, numbers, and special characters.');
        }
        if (error.message.includes('Invalid phone number')) {
          throw new Error(error.message);
        }
      }
      
      throw new Error('Account creation failed. Please try again or contact support if the problem persists.');
    }
  },

  /**
   * Verify email address with confirmation code
   */
  async verifyEmail(email: string, confirmationCode: string): Promise<EmailVerificationResult> {
    if (getMockMode()) {
      authLogger.info('🔍 RegistrationService: Using mock mode for email verification');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Mock validation
      if (confirmationCode !== '123456') {
        throw new Error('Invalid verification code');
      }
      
      return {
        success: true,
        message: 'Email verified successfully (mock mode)'
      };
    }

    try {
      authLogger.info('🔍 RegistrationService: Verifying email for:', email);
      
      const result = await cognitoRegistrationService.confirmSignUp(email, confirmationCode);
      
      if (!result.success) {
        throw new Error(result.message || 'Email verification failed');
      }
      
      authLogger.info('🔍 RegistrationService: Email verification completed');
      
      return {
        success: true,
        message: 'Email verified successfully'
      };
      
    } catch (error) {
      authLogger.error('❌ RegistrationService: Email verification failed:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('CodeMismatchException')) {
          throw new Error('Invalid verification code. Please check and try again.');
        }
        if (error.message.includes('ExpiredCodeException')) {
          throw new Error('Verification code has expired. Please request a new one.');
        }
        if (error.message.includes('LimitExceededException')) {
          throw new Error('Too many attempts. Please wait before trying again.');
        }
      }
      
      throw new Error('Email verification failed. Please try again.');
    }
  },

  /**
   * Resend verification code
   */
  async resendVerificationCode(email: string): Promise<EmailVerificationResult> {
    if (getMockMode()) {
      authLogger.info('🔍 RegistrationService: Using mock mode for resend verification code');
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return {
        success: true,
        message: 'Verification code sent (mock mode)'
      };
    }

    try {
      authLogger.info('🔍 RegistrationService: Resending verification code for:', email);
      
      const result = await cognitoRegistrationService.resendConfirmationCode(email);
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to resend verification code');
      }
      
      authLogger.info('🔍 RegistrationService: Verification code resent');
      
      return {
        success: true,
        message: 'Verification code sent successfully'
      };
      
    } catch (error) {
      authLogger.error('❌ RegistrationService: Failed to resend verification code:', error);
      throw new Error('Failed to resend verification code. Please try again.');
    }
  },


  /**
   * Get current user data after successful registration/login
   */
  async getCurrentUserData() {
    if (getMockMode()) {
      return {
        id: 'mock-user-123',
        name: 'Unknown',
        email: 'demo@fabrik28.ai',
        firstName: 'N/A',
        lastName: 'N/A',
        company: 'N/A',
        tenantId: 'mock-tenant-123',
        useIcon: true,
        role: 'Admin'
      };
    }

    try {
      const user = await authService.getCurrentUser();
      if (!user) {
        throw new Error('No authenticated user found');
      }
      
      // Extract tenant information from user attributes
      const tenantId = user.attributes?.['custom:tenantId'] || '';
      
      // Check if given_name and family_name are default values
      const hasRealName = user.attributes?.given_name && user.attributes?.family_name && 
                         user.attributes?.given_name !== 'Unavailable' && user.attributes?.family_name !== 'Unavailable' &&
                         user.attributes?.given_name !== 'N/A' && user.attributes?.family_name !== 'N/A';
      
      return {
        id: user.userId,
        name: hasRealName 
          ? `${user.attributes?.given_name} ${user.attributes?.family_name}`.trim() 
          : 'N/A',
        email: user.attributes?.email || user.username,
        firstName: user.attributes?.given_name,
        lastName: user.attributes?.family_name,
        company: 'N/A',
        useIcon: !hasRealName,
        tenantId: tenantId,
      };
    } catch (error) {
      authLogger.error('❌ RegistrationService: Failed to get user data:', error);
      throw error;
    }
  }
};
